import { useState, useEffect } from "react";
import { Package, Loader2, Search, CheckCircle, XCircle, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface OrderItem {
  id: string;
  product_name: string;
  variant_info: string | null;
  cover_image: string | null;
  quantity: number;
  unit_price: number;
}

interface ShippingAddress {
  full_name: string;
  address_line: string;
  city: string;
  postal_code: string;
  province: string;
  phone: string | null;
}

interface Order {
  id: string;
  user_id: string;
  total_price: number;
  status: string;
  created_at: string;
  notes: string | null;
  shipping_address_id: string | null;
  items: OrderItem[];
  profile?: {
    first_name: string | null;
    last_name: string | null;
    phone_number: string | null;
    email: string | null;
  };
  shipping_address?: ShippingAddress | null;
}

const statusOptions = [
  { value: "pending", label: "In attesa di conferma" },
  { value: "confirmed", label: "Confermato" },
  { value: "cancelled", label: "Annullato" },
  { value: "paid", label: "Pagato" },
  { value: "preparing", label: "In preparazione" },
  { value: "packaging", label: "Imballaggio" },
  { value: "ready_pickup", label: "Pronto da ritirare" },
  { value: "ready_delivery", label: "Pronto per la consegna" },
  { value: "picked_up", label: "Ritirato" },
  { value: "delivered", label: "Consegnato" },
];

export const AdminOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data: ordersData, error } = await supabase
        .from('orders')
        .select('id, user_id, total_price, status, created_at, notes, shipping_address_id')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch order items
      const orderIds = (ordersData || []).map(o => o.id);
      const { data: itemsData } = await supabase
        .from('order_items')
        .select('id, order_id, product_name, variant_info, cover_image, quantity, unit_price')
        .in('order_id', orderIds);

      const itemsByOrder = new Map<string, OrderItem[]>();
      (itemsData || []).forEach(item => {
        const existing = itemsByOrder.get(item.order_id) || [];
        existing.push(item);
        itemsByOrder.set(item.order_id, existing);
      });

      // Fetch profiles
      const userIds = [...new Set((ordersData || []).map(o => o.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, phone_number, email')
        .in('user_id', userIds);
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      // Fetch shipping addresses
      const addressIds = (ordersData || []).map(o => o.shipping_address_id).filter(Boolean) as string[];
      let addressMap = new Map<string, ShippingAddress>();
      if (addressIds.length > 0) {
        const { data: addressesData } = await supabase
          .from('shipping_addresses')
          .select('id, full_name, address_line, city, postal_code, province, phone')
          .in('id', addressIds);
        addressMap = new Map((addressesData || []).map(a => [a.id, a]));
      }

      const ordersWithData: Order[] = (ordersData || []).map(order => ({
        ...order,
        items: itemsByOrder.get(order.id) || [],
        profile: profileMap.get(order.user_id) || undefined,
        shipping_address: order.shipping_address_id ? addressMap.get(order.shipping_address_id) || null : null,
      }));

      setOrders(ordersWithData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    setUpdatingId(orderId);
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) throw error;

      const order = orders.find(o => o.id === orderId);

      setOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, status: newStatus } : o
      ));

      // Send push notification to customer
      if (order) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const { data: subs } = await supabase
              .from("push_subscriptions")
              .select("*")
              .eq("user_id", order.user_id);
            
            if (subs && subs.length > 0) {
              const statusLabel = statusOptions.find(s => s.value === newStatus)?.label || newStatus;
              await supabase.functions.invoke("send-push-notification", {
                headers: { Authorization: `Bearer ${session.access_token}` },
                body: {
                  subscription: subs[0],
                  title: newStatus === 'confirmed' ? "✅ Ordine confermato!" : newStatus === 'cancelled' ? "❌ Ordine annullato" : "📦 Aggiornamento ordine",
                  body: newStatus === 'confirmed' 
                    ? "Il tuo ordine è stato confermato. Puoi procedere con il pagamento."
                    : `Stato aggiornato: ${statusLabel}`,
                  url: "/profile",
                },
              });
            }
          }
        } catch (e) {
          console.log("Push notification error:", e);
        }
      }

      toast.success('Stato ordine aggiornato');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error("Errore nell'aggiornamento");
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getDisplayName = (profile: Order['profile']) => {
    if (!profile) return 'Cliente';
    const firstName = profile.first_name || '';
    const lastInitial = profile.last_name ? profile.last_name.charAt(0).toUpperCase() + '.' : '';
    return firstName ? `${firstName} ${lastInitial}`.trim() : 'Cliente';
  };

  const filteredOrders = orders.filter(order => {
    const searchLower = searchQuery.toLowerCase();
    const displayName = getDisplayName(order.profile).toLowerCase();
    const itemNames = order.items.map(i => i.product_name.toLowerCase()).join(' ');
    return (
      displayName.includes(searchLower) ||
      itemNames.includes(searchLower) ||
      order.id.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-4 border-b border-border/50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Cerca ordine..." className="pl-10" />
        </div>
      </div>

      {/* Orders List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground font-medium">{searchQuery ? "Nessun ordine trovato" : "Nessun ordine"}</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className="p-4 rounded-xl bg-card/80 border border-border/50 space-y-3">
              {/* Customer info */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  👤 {getDisplayName(order.profile)}
                </p>
                {order.profile?.email && (
                  <p className="text-xs text-blue-500">✉️ {order.profile.email}</p>
                )}
                {order.profile?.phone_number && (
                  <p className="text-xs text-green-600">📞 {order.profile.phone_number}</p>
                )}
              </div>

              {/* Shipping address */}
              {order.shipping_address && (
                <div className="p-2 rounded-lg bg-muted/50 text-xs text-muted-foreground flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    {order.shipping_address.full_name} — {order.shipping_address.address_line}, {order.shipping_address.postal_code} {order.shipping_address.city} ({order.shipping_address.province})
                    {order.shipping_address.phone && ` • ${order.shipping_address.phone}`}
                  </span>
                </div>
              )}

              {/* Items */}
              {order.items.map(item => (
                <div key={item.id} className="flex items-start gap-3">
                  {item.cover_image && (
                    <img src={item.cover_image} alt={item.product_name} className="w-12 h-12 rounded-lg object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-foreground truncate">{item.product_name}</p>
                    {item.variant_info && <p className="text-xs text-muted-foreground">{item.variant_info}</p>}
                    <p className="text-xs text-muted-foreground">Qtà: {item.quantity} • €{(item.unit_price * item.quantity).toFixed(2)}</p>
                  </div>
                </div>
              ))}

              {order.notes && (
                <p className="text-xs text-muted-foreground italic">📝 {order.notes}</p>
              )}

              <div className="flex items-center justify-between text-sm">
                <span className="font-bold">€{order.total_price.toFixed(2)}</span>
                <span className="text-xs text-muted-foreground">{formatDate(order.created_at)}</span>
              </div>

              {/* Quick actions for pending orders */}
              {order.status === 'pending' && (
                <div className="flex gap-2 pt-2 border-t border-border/30">
                  <Button
                    size="sm"
                    className="flex-1 gap-1.5"
                    onClick={() => handleStatusChange(order.id, 'confirmed')}
                    disabled={updatingId === order.id}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Conferma
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="flex-1 gap-1.5"
                    onClick={() => handleStatusChange(order.id, 'cancelled')}
                    disabled={updatingId === order.id}
                  >
                    <XCircle className="w-4 h-4" />
                    Annulla
                  </Button>
                </div>
              )}

              {/* Status selector for non-pending */}
              {order.status !== 'pending' && (
                <div className="pt-2 border-t border-border/30">
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Stato ordine</label>
                  <Select
                    value={order.status}
                    onValueChange={(value) => handleStatusChange(order.id, value)}
                    disabled={updatingId === order.id}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
